import {useCallback, useMemo, useState} from 'react'

import type {PinControls} from '../components/index/PinViewMenuItem'
import {VALUES} from '../constants'
import type {SavedView, SavedViewId, SavedViewsResponse} from '../types'
import {isPinLimitError, pinnedMutationErrorMessage, type SavedViewMutationOptions} from '../utils/saved-view-mutations'
import {viewVisibility} from '../utils/views-visibility'
import {usePinnedSavedViews} from './use-pinned-saved-views'
import type {SavedViewPinSource} from './use-saved-view-analytics'
import {useSetSavedViewPinnedMutation} from './use-set-saved-view-pinned-mutation'

export interface SavedViewPinControlsResult {
  pinControls?: PinControls
  pinnedCount: number | undefined
  pinErrorMessage: string | null
  retryPin?: () => void
  isPinRetrying: boolean
}

interface PinFailure {
  error: Error
  id: SavedViewId
  name: string
  pinned: boolean
}

export function useSavedViewPinControls({
  owner,
  repo,
  enabled,
  source,
  initialData,
  perVisibilityPinCapEnabled = false,
}: SavedViewMutationOptions & {
  enabled: boolean
  source: SavedViewPinSource
  initialData?: SavedViewsResponse
  perVisibilityPinCapEnabled?: boolean
}): SavedViewPinControlsResult {
  const [failure, setFailure] = useState<PinFailure | null>(null)
  const [retryingId, setRetryingId] = useState<SavedViewId | null>(null)

  const clearFailureFor = useCallback((id: SavedViewId) => {
    setFailure(prev => (prev?.id === id ? null : prev))
  }, [])

  const {mutate, mutateAsync} = useSetSavedViewPinnedMutation({
    owner,
    repo,
    source,
    onPinMutate: ({id}) => clearFailureFor(id),
    onPinSuccess: ({id}) => clearFailureFor(id),
    onPinError: ({error, id, pinned, name}) => setFailure({error, id, pinned, name}),
  })

  const {pinnedCount, pinnedCountsByVisibility, isFetching} = usePinnedSavedViews({owner, repo, enabled, initialData})
  const pinLimitLoading = enabled && pinnedCount === undefined && isFetching
  const pinnedCountForView = useCallback(
    (view: SavedView) => (perVisibilityPinCapEnabled ? pinnedCountsByVisibility?.[viewVisibility(view)] : pinnedCount),
    [perVisibilityPinCapEnabled, pinnedCount, pinnedCountsByVisibility],
  )
  const pinLimitForView = useCallback(
    (view: SavedView) => {
      if (!perVisibilityPinCapEnabled) return VALUES.maxPinnedViews
      return viewVisibility(view) === 'personal' ? VALUES.maxPersonalPinnedViews : VALUES.maxTeamPinnedViews
    },
    [perVisibilityPinCapEnabled],
  )

  const togglePin = useCallback(
    (view: SavedView) => {
      mutate({id: view.id, name: view.name, pinned: !view.pinned})
    },
    [mutate],
  )

  const retryPin = useCallback(() => {
    if (!failure) return
    const {id, name, pinned} = failure
    setRetryingId(id)
    const clearRetry = () => setRetryingId(prev => (prev === id ? null : prev))
    async function runRetry() {
      try {
        await mutateAsync({id, name, pinned})
      } catch {
        clearRetry()
        return
      }
      clearRetry()
    }
    void runRetry()
  }, [mutateAsync, failure])

  const pinControls = useMemo<PinControls | undefined>(
    () => (enabled ? {togglePin, pinnedCountForView, pinLimitForView, pinLimitLoading} : undefined),
    [enabled, togglePin, pinnedCountForView, pinLimitForView, pinLimitLoading],
  )

  const canRetry = failure != null && !isPinLimitError(failure.error, failure.pinned)
  const pinErrorMessage = failure ? pinnedMutationErrorMessage(failure.error, failure.pinned, failure.name) : null
  const isPinRetrying = retryingId != null && (failure == null || failure.id === retryingId)
  const showRetry = canRetry || isPinRetrying

  return {pinControls, pinnedCount, pinErrorMessage, retryPin: showRetry ? retryPin : undefined, isPinRetrying}
}
